import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Star, Trophy, Target, Info } from 'lucide-react';

const HIGHLIGHT_VARIANTS = {
  streak: {
    icon: Star,
    color: 'text-yellow-500 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/30',
    hoverColor: 'hover:bg-yellow-100 dark:hover:bg-yellow-900/50'
  },
  achievement: {
    icon: Trophy,
    color: 'text-green-500 dark:text-green-400 bg-green-50 dark:bg-green-900/30',
    hoverColor: 'hover:bg-green-100 dark:hover:bg-green-900/50'
  },
  goal: {
    icon: Target,
    color: 'text-blue-500 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30',
    hoverColor: 'hover:bg-blue-100 dark:hover:bg-blue-900/50'
  }
};

const getHighlightsMocks = (t) => [
  {
    type: 'achievement',
    title: t('weeklyHighlights.highlights.streak.title'),
    description: t('weeklyHighlights.highlights.streak.description'),
    details: t('weeklyHighlights.highlights.streak.details')
  },
  {
    type: 'achievement',
    title: t('weeklyHighlights.highlights.achievement.title'),
    description: t('weeklyHighlights.highlights.achievement.description'),
    details: t('weeklyHighlights.highlights.achievement.details')
  },
  {
    type: 'achievement',
    title: t('weeklyHighlights.highlights.goal.title'),
    description: t('weeklyHighlights.highlights.goal.description'),
    details: t('weeklyHighlights.highlights.goal.details')
  }
];

export function WeeklyHighlights() {
  const { t } = useTranslation();
  const highlightsMocks = getHighlightsMocks(t);
  const [expandedIndex, setExpandedIndex] = React.useState<number | null>(null);

  const toggleExpand = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
          {t('weeklyHighlights.title')}
        </h2>
        <Info
          className="w-5 h-5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer"
          aria-label="Weekly highlights information"
        />
      </div>

      <div className="space-y-4">
        {highlightsMocks.map((item, index) => {
          const variant = HIGHLIGHT_VARIANTS[item.type];
          const Icon = variant.icon;
          const isExpanded = expandedIndex === index;

          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`
                rounded-xl p-4 transition-all duration-300 
                ${variant.color} ${variant.hoverColor}
                border border-transparent hover:border-current
                cursor-pointer group
              `}
              onClick={() => toggleExpand(index)}
            >
              <div className="flex items-start space-x-4">
                <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center
                  ${variant.color}
                  group-hover:scale-105 transition-transform
                `}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {item.description}
                  </p>

                  {/* Expandable Details */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-2 pt-2 border-t border-current/10"
                      >
                        <p className="text-xs text-gray-500 dark:text-gray-300">
                          {item.details}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
}

export default WeeklyHighlights;